import React, { ReactElement } from 'react';
import { useInvoicesQuery, InvoiceStatus, InvoiceFragment } from '$/GraphQL/schema/schema';
import { Column, Table, AutoSizer, TableCellProps } from 'react-virtualized';
import { css } from '@emotion/react';
import { useList } from '$/hooks/useList';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import tw from 'twin.macro';
import Loading from '$/components/Loading';
import Button from '$/components/ui/Button/Button';
import Settings from '$/components/ui/Settings';
import EDate from '$/shared/frontend/utils/EDate';

const invoiceStatusDiv = (invoice: InvoiceFragment): JSX.Element => {
  let className = '';
  let status = 'UNKNOWN';

  if (invoice.amount === 0 && invoice.endingBalance - invoice.startingBalance < 0) {
    status = 'CREDIT USED';
  }
  switch (invoice.status) {
    case InvoiceStatus.Paid:
      status = 'PAID';
      break;
    case InvoiceStatus.Draft:
    case InvoiceStatus.Open:
    case InvoiceStatus.Uncollectible:
    case InvoiceStatus.Void:
      status = 'UNPAID';
      className = 'text-color-error';
      break;
    case InvoiceStatus.Deleted:
      status = 'VOIDED';
      break;
  }

  if (invoice.amount_refunded) {
    status = 'REFUNDED';
  }

  return <div className={`py-2 text-center paid - status ${className}`}>{status}</div>;
};

export const InvoiceHistory: React.FC = () => {
  const { md: isMd } = useWindowSizeInfo();

  const [formatter, setFormatter] = React.useState(
    new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: 'USD',
    })
  );
  const { data, onLoadMore, loading } = useList({
    queryHook: useInvoicesQuery,
    getVars: (nextCursor?: string) => ({ filter: { nextCursor } }),
    cursorPath: 'invoices.nextCursor',
    dataPath: 'invoices.invoices',
    notifyOnNetworkStatusChange: true,
  });
  React.useEffect(() => {
    if (data?.invoices?.invoices[0]) {
      setFormatter(
        new Intl.NumberFormat(navigator.language, {
          style: 'currency',
          currency: data.invoices.invoices[0].currency,
        })
      );
    }
  }, [data?.invoices.invoices]);

  const onScroll = ({
    scrollTop,
    scrollHeight,
    clientHeight,
  }: {
    clientHeight: number;
    scrollHeight: number;
    scrollTop: number;
  }): void => {
    const isScrollToEnd = scrollTop > scrollHeight - clientHeight - 50;
    if (isScrollToEnd) {
      onLoadMore();
    }
  };

  const invoices = data?.invoices?.invoices;

  return (
    <>
      <Settings.Title>Invoices</Settings.Title>
      {invoices && invoices.length > 0 && (
        <div
          css={
            !isMd
              ? css`
                  overflow: scroll;
                  width: 100%;
                  .paid-status {
                    ${tw`text-xs`}
                  }
                `
              : undefined
          }
        >
          <AutoSizer disableHeight disableWidth={false}>
            {({ width }: { width: number }): ReactElement => {
              const rowHeight = 42;
              const rowCount = invoices.length;
              const height = (rowCount + 1) * rowHeight;
              if (!isMd) {
                width = 590;
              }

              return (
                <Table
                  className='table-auto pt-2e md:pt-0 mt-1e'
                  rowClassName='flex text-left'
                  width={width}
                  height={height}
                  onScroll={onScroll}
                  headerHeight={rowHeight}
                  rowHeight={rowHeight}
                  headerClassName={`text-gray-700 text-xs md:text-sm font-semibold uppercase text-left self-end ml-0 mr-0`}
                  rowCount={rowCount}
                  rowGetter={({ index }: { index: number }): unknown => invoices[index]}
                  css={css`
                    .ReactVirtualized__Table__rowColumn {
                      margin-right: 0;
                    }
                  `}
                >
                  <Column
                    dataKey={'createdAt'}
                    width={80}
                    headerRenderer={(): ReactElement => <div>Date</div>}
                    cellRenderer={({ cellData }: TableCellProps): ReactElement => {
                      return (
                        <div className='text-xs md:text-normal uppercase text-left font-light py-2 text-gray-700'>
                          {new EDate(cellData).format('PP')}
                        </div>
                      );
                    }}
                  />
                  <Column
                    dataKey={'status'}
                    width={85}
                    headerRenderer={(): ReactElement => <div className='text-center'>Status</div>}
                    cellRenderer={(cell: TableCellProps): ReactElement => {
                      return invoiceStatusDiv(cell.rowData as InvoiceFragment);
                    }}
                  />
                  <Column
                    dataKey={'startingBalance'}
                    width={90}
                    headerRenderer={(): ReactElement => <div className='text-right'>Total Due</div>}
                    cellRenderer={(cell: TableCellProps): ReactElement => {
                      return (
                        <div className='text-right py-2'>
                          {formatter.format(
                            Math.max(
                              (cell.rowData as InvoiceFragment).amount +
                                (cell.rowData as InvoiceFragment).endingBalance -
                                (cell.rowData as InvoiceFragment).startingBalance,
                              0
                            )
                          ) ?? '$0.00'}
                        </div>
                      );
                    }}
                  />
                  <Column
                    dataKey={'startingBalance'}
                    width={90}
                    headerRenderer={(): ReactElement => (
                      <div className='text-right'>Credit Applied</div>
                    )}
                    cellRenderer={(cell: TableCellProps): ReactElement => {
                      return (
                        <div className='text-right py-2'>
                          {formatter.format(
                            Math.max(
                              (cell.rowData as InvoiceFragment).endingBalance -
                                (cell.rowData as InvoiceFragment).startingBalance,
                              0
                            )
                          ) ?? '$0.00'}
                        </div>
                      );
                    }}
                  />
                  <Column
                    dataKey={'amount'}
                    width={90}
                    headerRenderer={(): ReactElement => (
                      <div className='text-right'>Amount Charged</div>
                    )}
                    cellRenderer={({ cellData }: TableCellProps): ReactElement => {
                      return <div className='text-right py-2'>{formatter.format(cellData)}</div>;
                    }}
                  />
                  <Column
                    dataKey={'endingBalance'}
                    width={90}
                    headerRenderer={(): ReactElement => (
                      <div className='text-right'>Available Credit</div>
                    )}
                    cellRenderer={({ cellData }: TableCellProps): ReactElement => {
                      return (
                        <div className='text-right py-2'>
                          {formatter.format(Math.abs(cellData))}
                        </div>
                      );
                    }}
                  />
                  <Column
                    dataKey={'invoiceURL'}
                    width={65}
                    flexGrow={1}
                    cellRenderer={({ cellData }: TableCellProps): ReactElement => {
                      return (
                        <Button
                          className='flex w-full justify-end py-2'
                          css={css`
                            transform-origin: right center;
                          `}
                        >
                          <a className='text-blue-3' href={cellData} target='_blank'>
                            View
                          </a>
                        </Button>
                      );
                    }}
                  />
                </Table>
              );
            }}
          </AutoSizer>
        </div>
      )}
      {loading && <Loading local className='w-3e h-3e' />}
    </>
  );
};
